/**
 * ship_engineering_client.ts — the client half of ENGINEERING CREW AND SUBSYSTEMS (space campaign slice G, 2026-09-30;
 * flag `crew_engineering_v1`, ABSENT = ON; the rules are `@shared/ship_engineering.ts`).
 *
 * What the SERVER owns: the two groups per hull life (`ship_subsystem`), the field patch and its kit in custody
 * (`subsystem_job`), one receipt per request (`subsystem_receipt`), the crew leases and their epochs (`crew_lease`), who
 * routes power (`ship_power`), every pilot's subsystem selection (`subsystem_aim`). What this file does:
 *   · the DAMAGE-TAKEN path hands it what the shields let through; it predicts the §18.7 split with the server's own
 *     function (the server picks the group — an NPC attacker's from its key, a PvP hit's from the attacker's stored aim)
 *     and REPORTS the blow; the hull takes the rest. The prediction is reconciled to the row when it lands.
 *   · the EFFECTS the flight and the guns read (a degraded drive answers slower, a disabled one limps; degraded cannons
 *     cycle slower, disabled ones are silent) — always from the row, never a local guess beyond one report's flight.
 *   · the FIELD PATCH: picks a kit (your own, else the captain's within your lease's budget), starts the job, beats it at
 *     ~1 Hz while it runs (the server meters the 8 s), cancels, and reads the outcome from the receipt; the OWNER's client
 *     applies a hull receipt's ≤ 10 HP to its own pool, once.
 *   · the LEASES: invite / accept / decline / revoke / release, the engineer's power routing (epoch-fenced), the captain
 *     following the row and reclaiming with 1–4; who controls power is printed.
 *   · the DROID's finite field stock (nursing and V draw on it; a safe dock restocks it).
 *   · the CONFIRMED-HIT cue: only when the server's own record moved for my shot (a `space_pvp_hit` I made, a `war_life`
 *     whose last hit is mine losing hp, a `den_slot` I hunt losing hp) — the local spark stays the predicted cue.
 * The HUD strip and the engineer overlay render from `view()` (lib/engineering_panel.ts).
 */
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { keyHint } from './keybinds.js';
import { sfx } from './sfx.js';
import {
  ENGINEERING_FLAG, FIELD_CAP, FIELD_PATCH_MS, GROUP_MAX, KIT_KEY, KIT_BUDGET_MAX, SUBSYSTEM_GROUPS, ROLE_SEAT,
  allocateDamage, clampHealth, droidFieldStock, engineEffect, groupLine, groupState, isSubsystemGroup, npcAimGroup,
  patchRefusal, weaponsEffect, worstPatchable,
  type AimGroup, type CrewRole, type FlightEffect, type GroupHealth, type GroupLine, type GroupState, type SubsystemGroup,
} from '@shared/ship_engineering.js';

type Conn = NonNullable<typeof atlas.conn>;
type Db = Conn['db'];
export type SubRow = NonNullable<ReturnType<Db['shipSubsystem']['shipId']['find']>>;
export type JobRow = NonNullable<ReturnType<Db['subsystemJob']['jobId']['find']>>;
export type ReceiptRow = NonNullable<ReturnType<Db['subsystemReceipt']['receiptKey']['find']>>;
export type LeaseRow = NonNullable<ReturnType<Db['crewLease']['leaseId']['find']>>;
export type PowerRow = NonNullable<ReturnType<Db['shipPower']['shipId']['find']>>;
type Reducers = Record<string, (a: Record<string, unknown>) => Promise<void>>;

export const ENG_SUBS = [
  'SELECT * FROM ship_subsystem', 'SELECT * FROM subsystem_job', 'SELECT * FROM subsystem_receipt',
  'SELECT * FROM crew_lease WHERE captain = :sender', 'SELECT * FROM crew_lease WHERE holder = :sender',
  'SELECT * FROM ship_power', 'SELECT * FROM subsystem_aim', 'SELECT * FROM space_pvp_hit WHERE attacker = :sender',
  'SELECT * FROM player_item WHERE owner = :sender', 'SELECT * FROM platform_flag',
];
const BEAT_MS = 1050;
const msOf = (t: { microsSinceUnixEpoch: bigint }): number => Number(t.microsSinceUnixEpoch / 1000n);

/** What SpaceModule lends the controller (a narrow host — the controller never reaches into the module). */
export interface EngHost {
  active(): boolean;
  /** My crew seat (THE CREW LAW row), or null when I fly my own hull. */
  seat(): { captainHex: string; seat: string } | null;
  hull(): { hp: number; max: number };
  addHull(n: number): void;
  /** Docked, inside a registered station's dock range, or by my own depot. */
  safeDock(): boolean;
  nearbyPilots(): Array<{ hex: string; name: string; d: number; chassis: string }>;
  applyPower(preset: string, byName: string): void;
  power(): string;
  droidLevel(): number;
  toast(t: string): void;
}

export interface EngView {
  on: boolean;
  role: 'pilot' | 'engineer' | 'gunner' | 'none';
  shipId: bigint | null;
  shipName: string;
  captainName: string;
  health: GroupHealth;
  lines: GroupLine[];
  judged: { engine: number; weapons: number };
  lastCause: string;
  power: { preset: string; by: string; role: string } | null;
  job: { jobId: bigint; target: string; pct: number; mine: boolean; actorName: string } | null;
  kits: { own: number; captain: number; budgetLeft: number };
  receipts: Array<{ text: string; outcome: string; at: number }>;
  leases: Array<{ leaseId: bigint; role: string; holderName: string; state: string; epoch: number; kitsUsed: number; kitBudget: number; mine: boolean }>;
  invitesToMe: Array<{ leaseId: bigint; role: string; captainName: string; expiresInS: number }>;
  myLease: { leaseId: bigint; role: string; epoch: number; captainName: string; kitBudget: number; kitsUsed: number } | null;
  nearby: Array<{ hex: string; name: string; d: number; chassis: string }>;
  canInvite: boolean;
  safeDock: boolean;
  needsDock: boolean;
  droid: { stock: number; max: number } | null;
  confirmedAt: number;
  patchWhy: Record<string, string | null>;
}

class ShipEngineering {
  private host: EngHost | null = null;
  private subConn: Conn | null = null;
  private readonly nonce = Math.random().toString(36).slice(2, 7);
  private seq = 0;
  /** One report's flight: the predicted health until the row catches up (or 2 s pass). */
  private predicted = new Map<string, { h: GroupHealth; until: number; baseAt: number }>();
  private prevState = new Map<SubsystemGroup, GroupState>();
  private lastBeatAt = 0;
  private seenReceipts = new Set<string>();
  private receiptsPrimed = false;
  private seenInvites = new Set<string>();
  private powerSeen = '';
  private droidStock: number | null = null;
  private droidMax = 0;
  private starting = false;
  private aimSent = 'hull';
  confirmedAt = 0;
  readonly stats = { reports: 0, reportsOk: 0, reportErrors: 0, lastError: '', confirmed: 0, starts: 0, beats: 0, receipts: 0, hullApplied: 0, routes: 0 };

  attach(host: EngHost): void { this.host = host; }
  get conn(): Conn | null { return atlas.conn ?? null; }
  private get red(): Reducers { return this.conn!.reducers as unknown as Reducers; }

  /** Subscribe once per connection; hook the confirmed-hit cues. */
  ensure(): void {
    const c = this.conn;
    if (!c?.identity || this.subConn === c) return;
    this.subConn = c;
    this.receiptsPrimed = false;
    try { c.subscriptionBuilder().onApplied(() => this.primeReceipts()).subscribe(ENG_SUBS); } catch { /* pre-publish */ }
    const meHex = c.identity.toHexString();
    try {
      c.db.spacePvpHit.onInsert((_x, row) => { if (row.attacker.isEqual(c.identity!)) this.confirmed('pvp'); });
      c.db.warLife.onUpdate((_x, old, row) => { if (row.lastBy === meHex && row.hp < old.hp) this.confirmed('war'); });
      c.db.denSlot.onUpdate((_x, old, row) => { if (row.target.isEqual(c.identity!) && row.hp < old.hp) this.confirmed('den'); });
    } catch { /* pre-publish bindings */ }
  }
  private primeReceipts(): void {
    const c = this.conn;
    if (!c) return;
    try { for (const r of c.db.subsystemReceipt.iter()) this.seenReceipts.add(r.receiptKey); } catch { /* */ }
    try { for (const l of c.db.crewLease.iter()) if (l.state === 'invited') this.seenInvites.add(String(l.leaseId)); } catch { /* */ }
    this.receiptsPrimed = true;
  }
  /** THE CONFIRMED-HIT CUE: the server's record moved for my shot — distinct from the predicted (local) spark. */
  private confirmed(kind: string): void {
    this.confirmedAt = performance.now();
    this.stats.confirmed++;
    sfx('sfx_target_lock', { gain: 0.32, rate: 1.55, cooldownMs: 120 });
    void kind;
  }

  on(): boolean {
    try { const f = this.conn?.db.platformFlag.key.find(ENGINEERING_FLAG); return f ? f.enabled : true; } catch { return true; }
  }
  private me(): Identity | null { return this.conn?.identity ?? null; }
  private nameOf(id: Identity): string {
    try { return this.conn?.db.account.identity.find(id)?.username ?? id.toHexString().slice(0, 6); } catch { return '?'; }
  }
  private activeShipOf(owner: Identity): { shipId: bigint; name: string; chassisKey: string } | null {
    try { for (const s of this.conn!.db.playerShip.iter()) if (s.owner.isEqual(owner) && s.active) return s; } catch { /* */ }
    return null;
  }
  /** The hull this pilot works on: my own (flying), or the captain's (seated). */
  private workHull(): { shipId: bigint; name: string; owner: Identity; seated: boolean } | null {
    const me = this.me();
    if (!me) return null;
    const seat = this.host?.seat();
    if (seat) {
      const cap = Identity.fromString(seat.captainHex);
      const s = this.activeShipOf(cap);
      return s ? { shipId: s.shipId, name: s.name, owner: cap, seated: true } : null;
    }
    const s = this.activeShipOf(me);
    return s ? { shipId: s.shipId, name: s.name, owner: me, seated: false } : null;
  }
  private row(shipId: bigint): SubRow | null { try { return this.conn?.db.shipSubsystem.shipId.find(shipId) ?? null; } catch { return null; } }
  health(shipId: bigint | null): GroupHealth {
    if (shipId === null) return { engine: GROUP_MAX, weapons: GROUP_MAX };
    const r = this.row(shipId);
    const base: GroupHealth = r ? { engine: clampHealth(r.engine), weapons: clampHealth(r.weapons) } : { engine: GROUP_MAX, weapons: GROUP_MAX };
    const p = this.predicted.get(String(shipId));
    if (!p) return base;
    const rowAt = r ? msOf(r.updatedAt) : 0;
    if (performance.now() > p.until || rowAt > p.baseAt) { this.predicted.delete(String(shipId)); return base; }
    return p.h;
  }
  private myHealth(): GroupHealth {
    const me = this.me();
    const s = me ? this.activeShipOf(me) : null;
    return this.health(s?.shipId ?? null);
  }

  // ── EFFECTS (the flight and the guns read these; seated crew fly nothing of their own) ───────────────────────────
  flight(): FlightEffect {
    if (!this.on() || this.host?.seat()) return { accel: 1, limp: null, boost: true, cruise: true };
    return engineEffect(this.myHealth().engine);
  }
  gunsOnline(): boolean { return !this.on() || !!this.host?.seat() || weaponsEffect(this.myHealth().weapons).online; }
  gunCdMult(): number { return !this.on() || this.host?.seat() ? 1 : weaponsEffect(this.myHealth().weapons).cdMult; }
  private gunsNoteAt = 0;
  gunsDownNote(): void {
    if (performance.now() - this.gunsNoteAt < 2500) return;
    this.gunsNoteAt = performance.now();
    sfx('sfx_weapon_dry', { gain: 0.32, cooldownMs: 300 });
    this.host?.toast(`✖ Cannons DISABLED — they cannot fire; patch them (${keyHint('spacePatch')}) or restore at a dock — the missile rack still fires`);
  }

  // ── THE DAMAGE-TAKEN PATH ────────────────────────────────────────────────────────────────────────────────────────
  /** What the shields let through → the hull's share. The server gets the report; the split is predicted with ITS rule. */
  onDamage(postShield: number, source?: { kind: string; key: string; hitId?: bigint }): number {
    const c = this.conn, me = this.me();
    if (!c || !me || !this.on() || this.host?.seat() || !(postShield > 0)) return postShield;
    const ship = this.activeShipOf(me);
    if (!ship) return postShield;
    const kind = source?.kind ?? 'unknown', ref = (source?.key ?? '').slice(0, 64);
    let selected: AimGroup = 'hull';
    if (kind === 'player') {
      if (source?.hitId && source.hitId > 0n) {
        try { const a = c.db.subsystemAim.identity.find(Identity.fromString(ref))?.aim; if (a === 'engine' || a === 'weapons' || a === 'sensors') selected = a; } catch { /* */ }
      }
    } else selected = npcAimGroup(kind, ref);
    const health = this.health(ship.shipId);
    const dmg = Math.min(postShield, 60);
    const alloc = allocateDamage(dmg, selected, health);
    if (alloc.group && alloc.applied > 0) {
      const next = { ...health, [alloc.group]: health[alloc.group] - alloc.applied } as GroupHealth;
      const r = this.row(ship.shipId);
      this.predicted.set(String(ship.shipId), { h: next, until: performance.now() + 2000, baseAt: r ? msOf(r.updatedAt) : 0 });
    }
    this.stats.reports++;
    this.red.reportShipDamage({ shipId: ship.shipId, postShield: dmg, kind, ref, hitId: source?.hitId ?? 0n })
      .then(() => { this.stats.reportsOk++; })
      .catch((e: Error) => { this.stats.reportErrors++; this.stats.lastError = String(e.message ?? e); });
    return alloc.hull + (postShield - dmg);
  }
  /** T: the pilot's subsystem selection, stored on the server (it decides a judged PvP hit's group). */
  aim(sub: string): void {
    const a = sub === 'engines' ? 'engine' : sub === 'weapons' ? 'weapons' : 'hull';
    if (a === this.aimSent || !this.conn) return;
    this.aimSent = a;
    this.red.setSubsystemAim({ aim: a }).catch(() => { /* pre-publish */ });
  }

  // ── POWER ────────────────────────────────────────────────────────────────────────────────────────────────────────
  private myLeaseAs(role: CrewRole): LeaseRow | null {
    const me = this.me();
    if (!me) return null;
    try { for (const l of this.conn!.db.crewLease.iter()) if (l.holder.isEqual(me) && l.role === role && l.state === 'active') return l; } catch { /* */ }
    return null;
  }
  private crewOnMyShip(role: CrewRole): LeaseRow | null {
    const me = this.me();
    const ship = me ? this.activeShipOf(me) : null;
    if (!ship) return null;
    try { for (const l of this.conn!.db.crewLease.iter()) if (l.captain.isEqual(me!) && l.shipId === ship.shipId && l.role === role && l.state === 'active') return l; } catch { /* */ }
    return null;
  }
  /** An engineer's 1–4 routes the CAPTAIN's hull (epoch-fenced on the server). True = the press is handled here. */
  routesFor(preset: string): boolean {
    if (!this.on() || this.host?.seat()?.seat !== ROLE_SEAT.engineer) return false;
    const l = this.myLeaseAs('engineer');
    if (!l) { this.host?.toast('You hold no engineer lease on this hull'); return true; }
    this.stats.routes++;
    this.red.crewRoutePower({ leaseId: l.leaseId, epoch: l.epoch, preset })
      .then(() => this.host?.toast(`⚙ You route the captain's power → ${preset.toUpperCase()}`))
      .catch((e: Error) => this.host?.toast(`⚠ ${String(e.message ?? e)}`));
    return true;
  }
  /** A crewed captain's own press reclaims the routing (one writer, named). A solo pilot's stays local. */
  pilotSetPower(preset: string): void {
    if (!this.on() || !this.crewOnMyShip('engineer')) return;
    this.powerSeen = `${preset}:pilot`;
    this.red.shipSetPower({ preset }).catch(() => { /* the local preset already applied */ });
  }
  private followPower(): void {
    const me = this.me(), ship = me ? this.activeShipOf(me) : null;
    if (!me || !ship || this.host?.seat()) return;
    let pw: PowerRow | null = null;
    try { pw = this.conn!.db.shipPower.shipId.find(ship.shipId) ?? null; } catch { /* */ }
    if (!pw) return;
    const key = `${pw.preset}:${pw.writer.toHexString()}:${pw.epoch}`;
    if (key === this.powerSeen) return;
    this.powerSeen = key;
    if (!pw.writer.isEqual(me) && pw.preset !== this.host?.power()) this.host?.applyPower(pw.preset, this.nameOf(pw.writer));
  }

  // ── THE DROID'S FIELD STOCK ──────────────────────────────────────────────────────────────────────────────────────
  private droidReady(): void {
    const lvl = this.host?.droidLevel() ?? 0, max = droidFieldStock(lvl);
    if (max !== this.droidMax) { this.droidMax = max; this.droidStock = max; }
    if (this.host?.safeDock()) this.droidStock = max;
  }
  /** Passive nursing: what the droid may give this tick (flag off: everything — today's ship). */
  droidNurse(amount: number): number {
    if (!this.on() || !(amount > 0)) return amount;
    this.droidReady();
    const give = Math.min(amount, this.droidStock ?? 0);
    this.droidStock = (this.droidStock ?? 0) - give;
    return give;
  }
  /** V: the emergency patch, from the same stock. 0 = the stock is spent. */
  droidPatch(amount: number): number { return this.droidNurse(amount); }
  droidStockLine(): string { return this.on() && this.droidMax > 0 ? `droid stock ${Math.round(this.droidStock ?? 0)}/${this.droidMax} (a safe dock restocks it)` : ''; }

  // ── THE FIELD PATCH ──────────────────────────────────────────────────────────────────────────────────────────────
  private myJob(): JobRow | null {
    const me = this.me();
    if (!me) return null;
    try { for (const j of this.conn!.db.subsystemJob.iter()) if (j.actor.isEqual(me)) return j; } catch { /* */ }
    return null;
  }
  private kitsOf(owner: Identity): Array<{ itemId: bigint; serial: string }> {
    const out: Array<{ itemId: bigint; serial: string }> = [];
    try { for (const i of this.conn!.db.playerItem.iter()) if (i.owner.isEqual(owner) && i.itemKey === KIT_KEY) out.push(i); } catch { /* */ }
    return out;
  }
  private missingHull(): number { const h = this.host?.hull(); return h ? Math.max(0, h.max - h.hp) : 0; }
  /** Why `target` cannot be patched right now (null = it can). */
  patchWhy(target: string): string | null {
    if (!this.on()) return 'Ship engineering is off on this shard';
    const hull = this.workHull();
    if (!hull) return 'No hull to work on';
    const seat = this.host?.seat();
    if (seat && seat.seat !== ROLE_SEAT.engineer) return 'Only the engineer station repairs';
    const job = this.myJob();
    if (job) return `You are already patching the ${job.target}`;
    if (target === 'hull' && !hull.seated && this.missingHull() < 0.5) return 'The hull is whole';
    const r = patchRefusal(target, this.health(hull.shipId));
    if (r) return r;
    const me = this.me()!;
    const own = this.kitsOf(me).length;
    const lease = seat ? this.myLeaseAs('engineer') : null;
    const capLeft = lease ? Math.max(0, lease.kitBudget - lease.kitsUsed) : 0;
    if (own === 0 && (capLeft === 0 || this.kitsOf(hull.owner).length === 0)) return 'No repair kit — craft one (2 metal ingots + 1 field tool)';
    return null;
  }
  /** The cockpit shortcut: patch the worst group under the field cap, else the hull (solo), else say why not. */
  patchShortcut(): void {
    const job = this.myJob();
    if (job) { this.cancel(); return; }
    const hull = this.workHull();
    if (!hull) { this.host?.toast('No hull to work on'); return; }
    const g = worstPatchable(this.health(hull.shipId));
    const target = g ?? (!hull.seated && this.missingHull() >= 0.5 ? 'hull' : null);
    if (!target) {
      const h = this.health(hull.shipId);
      this.host?.toast(`Nothing a field patch can fix — engine ${h.engine}, weapons ${h.weapons} (the field cap is ${FIELD_CAP}; a dock restores the rest)`);
      return;
    }
    this.patch(target);
  }
  patch(target: string): void {
    const why = this.patchWhy(target);
    if (why) { this.host?.toast(`⚠ ${why}`); return; }
    if (this.starting) return;
    const hull = this.workHull()!;
    const me = this.me()!;
    const seat = this.host?.seat();
    const lease = seat ? this.myLeaseAs('engineer') : null;
    const own = this.kitsOf(me);
    const kit = own[0] ?? (lease ? this.kitsOf(hull.owner)[0] : undefined);
    if (!kit) { this.host?.toast('⚠ No repair kit — craft one (2 metal ingots + 1 field tool)'); return; }
    const requestId = `eng${this.nonce}-${++this.seq}`;
    this.starting = true;
    this.stats.starts++;
    this.red.startShipRepair({ shipId: hull.shipId, target, kitItemId: kit.itemId, requestId, leaseId: lease?.leaseId ?? 0n, epoch: lease?.epoch ?? 0 })
      .then(() => { this.lastBeatAt = performance.now(); sfx('sfx_ore_yield', { gain: 0.3, rate: 0.8, cooldownMs: 400 }); this.host?.toast(`⚒ Field patch on the ${target} — 8 s of exposed work (${keyHint('spacePatch')} again cancels; a hit stops it and returns the kit)`); })
      .catch((e: Error) => this.host?.toast(`⚠ ${String(e.message ?? e)}`))
      .finally(() => { this.starting = false; });
  }
  cancel(): void {
    const job = this.myJob();
    if (!job) return;
    this.red.cancelShipRepair({ jobId: job.jobId }).catch((e: Error) => this.host?.toast(`⚠ ${String(e.message ?? e)}`));
  }
  private dockAskAt = 0;
  restoreAtDock(auto = false): void {
    const me = this.me(), ship = me ? this.activeShipOf(me) : null;
    if (!ship) return;
    this.dockAskAt = performance.now();
    this.red.restoreSubsystemsAtDock({ shipId: ship.shipId })
      .then(() => { this.host?.toast('⚓ The dock crews reset the drive and the guns (safe-dock subsidy — free)'); sfx('sfx_ship_dock', { gain: 0.35, cooldownMs: 800 }); })
      .catch((e: Error) => { if (!auto) this.host?.toast(`⚠ ${String(e.message ?? e)}`); });
  }

  // ── THE LEASES ───────────────────────────────────────────────────────────────────────────────────────────────────
  invite(hex: string, role: CrewRole, kitBudget: number): void {
    this.red.crewInvite({ crew: Identity.fromString(hex), role, kitBudget: Math.max(0, Math.min(KIT_BUDGET_MAX, Math.round(kitBudget))) })
      .then(() => this.host?.toast(`✉ Invited — they accept from their engineering panel (${keyHint('spaceEngineering')})`))
      .catch((e: Error) => this.host?.toast(`⚠ ${String(e.message ?? e)}`));
  }
  accept(leaseId: bigint): void { this.red.crewAccept({ leaseId }).catch((e: Error) => this.host?.toast(`⚠ ${String(e.message ?? e)}`)); }
  decline(leaseId: bigint): void { this.red.crewDecline({ leaseId }).catch(() => { /* */ }); }
  revoke(leaseId: bigint): void { this.red.crewRevoke({ leaseId }).catch((e: Error) => this.host?.toast(`⚠ ${String(e.message ?? e)}`)); }
  release(): void { this.red.leaveTurret({}).catch((e: Error) => this.host?.toast(`⚠ ${String(e.message ?? e)}`)); }

  // ── THE TICK (every frame the sector is live) ────────────────────────────────────────────────────────────────────
  private lastViewAt = 0;
  private lastActive = 0;
  tick(nowMs: number): void {
    this.lastActive = nowMs;
    this.ensure();
    const c = this.conn, me = this.me();
    if (!c || !me) return;
    this.droidReady();
    // Beat my running patch (the server meters the 8 s; a beat sooner than 1 Hz is ignored there).
    const job = this.myJob();
    if (job && nowMs - this.lastBeatAt >= BEAT_MS) {
      this.lastBeatAt = nowMs;
      this.stats.beats++;
      this.red.shipRepairBeat({ jobId: job.jobId }).catch(() => { /* it ended — the receipt says how */ });
    }
    if (this.receiptsPrimed) this.readReceipts();
    this.followPower();
    this.noteInvites();
    this.cueStates();
    // A safe dock restores both groups on its own (the subsidy; the field patch stops at 60) — the pilot's hull only.
    if (this.on() && !this.host?.seat() && nowMs - this.dockAskAt > 15_000 && this.host?.safeDock()) {
      const h = this.myHealth();
      if (h.engine < GROUP_MAX || h.weapons < GROUP_MAX) this.restoreAtDock(true);
    }
  }
  /** True when the sector stopped ticking the controller (the HUD strip hides itself). */
  stale(nowMs: number): boolean { return nowMs - this.lastActive > 1000; }

  private readReceipts(): void {
    const me = this.me()!;
    try {
      for (const r of this.conn!.db.subsystemReceipt.iter()) {
        if (this.seenReceipts.has(r.receiptKey)) continue;
        this.seenReceipts.add(r.receiptKey);
        this.stats.receipts++;
        // The OWNER's client applies a hull receipt to its own pool — once (the hull is the pilot's instruments).
        if (r.outcome === 'hull' && r.owner.isEqual(me)) { const n = Math.min(r.add, this.missingHull()); if (n > 0) { this.host?.addHull(n); this.stats.hullApplied += n; } }
        if (r.actor.isEqual(me) || r.owner.isEqual(me)) this.host?.toast(receiptToast(r, r.actor.isEqual(me) ? 'you' : this.nameOf(r.actor)));
        if (r.outcome === 'patched' || r.outcome === 'hull') sfx('sfx_loot_pickup', { gain: 0.35, cooldownMs: 300 });
      }
    } catch { /* */ }
  }
  private noteInvites(): void {
    const me = this.me()!;
    try {
      for (const l of this.conn!.db.crewLease.iter()) {
        if (l.state !== 'invited' || !l.holder.isEqual(me) || this.seenInvites.has(String(l.leaseId))) continue;
        this.seenInvites.add(String(l.leaseId));
        sfx('sfx_target_lock', { gain: 0.3, cooldownMs: 600 });
        this.host?.toast(`✉ ${this.nameOf(l.captain)} invites you to crew their Sovereign as ${l.role.toUpperCase()} — open the engineering panel (${keyHint('spaceEngineering')}) to accept`);
      }
    } catch { /* */ }
  }
  /** The audio + text cue when a group of MY hull changes state (never colour alone). */
  private cueStates(): void {
    if (!this.on() || this.host?.seat()) return;
    const h = this.myHealth();
    for (const g of SUBSYSTEM_GROUPS) {
      const s = groupState(h[g]), was = this.prevState.get(g);
      this.prevState.set(g, s);
      if (!was || was === s) continue;
      const worse = (was === 'healthy' && s !== 'healthy') || (was === 'degraded' && s === 'disabled');
      const line = groupLine(g, h[g], { reroute: keyHint(g === 'engine' ? 'spacePowerEngines' : 'spacePowerWeapons'), patch: keyHint('spacePatch') });
      if (worse) { sfx(s === 'disabled' ? 'sfx_shield_down' : 'sfx_error', { gain: 0.45, rate: s === 'disabled' ? 0.8 : 1, cooldownMs: 700 }); this.host?.toast(`${line.glyph} ${line.text}`); }
      else this.host?.toast(`✓ ${g === 'engine' ? 'Engine' : 'Weapons'} back to ${s} (${h[g]})`);
    }
  }

  // ── THE VIEW (the HUD strip + the engineer overlay) ──────────────────────────────────────────────────────────────
  view(): EngView {
    const c = this.conn, me = this.me();
    const on = this.on();
    const empty: EngView = {
      on, role: 'none', shipId: null, shipName: '', captainName: '', health: { engine: GROUP_MAX, weapons: GROUP_MAX }, lines: [],
      judged: { engine: 0, weapons: 0 }, lastCause: '', power: null, job: null, kits: { own: 0, captain: 0, budgetLeft: 0 },
      receipts: [], leases: [], invitesToMe: [], myLease: null, nearby: [], canInvite: false, safeDock: false, needsDock: false,
      droid: null, confirmedAt: this.confirmedAt, patchWhy: {},
    };
    if (!c || !me) return empty;
    const seat = this.host?.seat() ?? null;
    const hull = this.workHull();
    const v: EngView = { ...empty };
    v.role = seat ? (seat.seat === ROLE_SEAT.engineer ? 'engineer' : 'gunner') : 'pilot';
    if (hull) {
      v.shipId = hull.shipId; v.shipName = hull.name; v.captainName = this.nameOf(hull.owner);
      v.health = this.health(hull.shipId);
      const keys = { reroute: keyHint('spacePowerEngines'), patch: keyHint('spacePatch') };
      v.lines = SUBSYSTEM_GROUPS.map((g) => groupLine(g, v.health[g], g === 'weapons' ? { ...keys, reroute: keyHint('spacePowerWeapons') } : keys));
      const r = this.row(hull.shipId);
      if (r) { v.judged = { engine: r.judgedEngine, weapons: r.judgedWeapons }; v.lastCause = r.lastCause; }
      try {
        const pw = c.db.shipPower.shipId.find(hull.shipId);
        if (pw) v.power = { preset: pw.preset, by: pw.writer.isEqual(me) ? 'you' : this.nameOf(pw.writer), role: pw.writerRole };
      } catch { /* */ }
      try {
        for (const j of c.db.subsystemJob.iter()) {
          if (j.shipId !== hull.shipId) continue;
          const sinceBeat = j.actor.isEqual(me) ? Math.min(BEAT_MS, performance.now() - this.lastBeatAt) : 0;
          v.job = { jobId: j.jobId, target: j.target, pct: Math.min(1, (j.workMs + sinceBeat) / FIELD_PATCH_MS), mine: j.actor.isEqual(me), actorName: j.actor.isEqual(me) ? 'you' : this.nameOf(j.actor) };
          if (v.job.mine) break;
        }
      } catch { /* */ }
      const lease = seat ? this.myLeaseAs('engineer') : null;
      v.kits = { own: this.kitsOf(me).length, captain: seat ? this.kitsOf(hull.owner).length : 0, budgetLeft: lease ? Math.max(0, lease.kitBudget - lease.kitsUsed) : 0 };
      for (const t of ['engine', 'weapons', 'hull']) v.patchWhy[t] = this.patchWhy(t);
    }
    try {
      const rs = [...c.db.subsystemReceipt.iter()].filter((r) => r.actor.isEqual(me) || r.owner.isEqual(me)).sort((a, b) => msOf(b.at) - msOf(a.at)).slice(0, 5);
      v.receipts = rs.map((r) => ({ text: receiptToast(r, r.actor.isEqual(me) ? 'you' : this.nameOf(r.actor)), outcome: r.outcome, at: msOf(r.at) }));
      const nowMs = Date.now();
      for (const l of c.db.crewLease.iter()) {
        if (l.state === 'ended') continue;
        if (l.captain.isEqual(me)) v.leases.push({ leaseId: l.leaseId, role: l.role, holderName: this.nameOf(l.holder), state: l.state, epoch: l.epoch, kitsUsed: l.kitsUsed, kitBudget: l.kitBudget, mine: false });
        if (l.holder.isEqual(me) && l.state === 'invited') v.invitesToMe.push({ leaseId: l.leaseId, role: l.role, captainName: this.nameOf(l.captain), expiresInS: Math.max(0, Math.round((msOf(l.expiresAt) - nowMs) / 1000)) });
        if (l.holder.isEqual(me) && l.state === 'active') v.myLease = { leaseId: l.leaseId, role: l.role, epoch: l.epoch, captainName: this.nameOf(l.captain), kitBudget: l.kitBudget, kitsUsed: l.kitsUsed };
      }
    } catch { /* */ }
    const myShip = this.activeShipOf(me);
    v.canInvite = !seat && myShip?.chassisKey === 'ship_sovereign' && on;
    v.nearby = v.canInvite ? (this.host?.nearbyPilots() ?? []) : [];
    v.safeDock = !!this.host?.safeDock();
    v.needsDock = !seat && (v.health.engine < GROUP_MAX || v.health.weapons < GROUP_MAX);
    if (this.droidMax > 0 && on) v.droid = { stock: Math.round(this.droidStock ?? 0), max: this.droidMax };
    return v;
  }
}

export function receiptToast(r: { outcome: string; target: string; add: number; before: number; after: number; detail: string; eligibleRestored: number }, who: string): string {
  if (r.outcome === 'patched') return `⚒ ${who === 'you' ? 'Your' : `${who}'s`} patch: ${r.target} ${r.before} → ${r.after}${r.eligibleRestored > 0 ? ` (${r.eligibleRestored} of it judged combat damage)` : ''}`;
  if (r.outcome === 'hull') return `⚒ ${who === 'you' ? 'Your' : `${who}'s`} hull patch: up to +${r.add} hull`;
  const verb = r.outcome === 'interrupted' ? 'interrupted' : r.outcome === 'cancelled' ? 'cancelled' : r.outcome === 'requote' ? 'not needed' : 'released';
  const detail = r.detail.startsWith(verb) ? r.detail.slice(verb.length).replace(/^\s*—\s*/, '') : r.detail; // the server's detail may lead with the verb
  return `↩ Patch ${verb} — ${detail}`;
}

export const shipEngineering = new ShipEngineering();
/** Probe lever (read-only + a few actions): the controller's state for verify probes. */
(globalThis as unknown as Record<string, unknown>).__engineering = {
  view: () => shipEngineering.view(),
  stats: () => ({ ...shipEngineering.stats }),
  on: () => shipEngineering.on(),
  patch: (t: string) => shipEngineering.patch(t),
  invite: (hex: string, role: CrewRole, budget = 0) => shipEngineering.invite(hex, role, budget),
  accept: (id: string | bigint) => shipEngineering.accept(BigInt(id)),
  isGroup: (g: string) => isSubsystemGroup(g),
};
